import type {
  DispatchAssignmentView,
  DispatchBoardDriver,
  DispatchBoardResponse,
} from "@/lib/api/dispatchTypes";
import type { OffPremiseGridEntry } from "@/lib/api/types";

/**
 * The till's reading of the dispatch board (driver backend B3 · P13).
 *
 * Pure: which bag a driver can be offered, what its tile says, how long an
 * offer has left on the SERVER's clock, and in which order the picker lists
 * the drivers. The board itself is `useDispatchBoard`; nothing here fetches.
 */

/** Assignment statuses that hold a bag (one per bill, the server's partial unique index). */
const LIVE = new Set(["OFFERED", "ACCEPTED", "PICKED_UP", "ARRIVED", "FAILED"]);

/** What the board knows about one bill. */
export type BillDispatch = {
  /** The live assignment, or a DELIVERED one whose bill is still unpaid. */
  current: DispatchAssignmentView | null;
  /** The newest declined / expired / withdrawn offer, only while nothing is current. */
  lastEnded: DispatchAssignmentView | null;
  /**
   * A delivery that ended AFTER pickup — the bag came back, or the till called
   * it off — while the bill is still CLOSED and nothing is current (B3 · P14
   * rework). The bag is at the counter, unpaid: never a cash bag to settle.
   */
  endedDelivery?: DispatchAssignmentView | null;
};

/** How a delivery ended after pickup, for the sitting's mark. */
export type EndedDeliveryKind = "RETURNED" | "CANCELLED";

export function endedDeliveryKind(entry: BillDispatch | undefined): EndedDeliveryKind | null {
  if (!entry || entry.current || !entry.endedDelivery) return null;
  return entry.endedDelivery.status === "RETURNED" ? "RETURNED" : "CANCELLED";
}

/** invoiceId → what the board says about it. Bills the board does not mention are absent. */
export function dispatchByInvoice(
  board: DispatchBoardResponse | undefined,
): Map<string, BillDispatch> {
  const map = new Map<string, BillDispatch>();
  if (!board) return map;
  for (const assignment of board.assignments) {
    const entry = map.get(assignment.invoiceId) ?? { current: null, lastEnded: null };
    // A live row wins over a delivered-unpaid one (a bag cannot have both, but
    // the board is read, not trusted).
    if (!entry.current || LIVE.has(assignment.status)) entry.current = assignment;
    map.set(assignment.invoiceId, entry);
  }
  for (const ended of board.endedDeliveries ?? []) {
    const entry = map.get(ended.invoiceId) ?? { current: null, lastEnded: null };
    if (entry.current) continue;
    entry.endedDelivery = ended;
    map.set(ended.invoiceId, entry);
  }
  for (const ended of board.endedOffers) {
    const entry = map.get(ended.invoiceId) ?? { current: null, lastEnded: null };
    if (entry.current) continue;
    const time = (view: DispatchAssignmentView) => Date.parse(view.endedAt ?? view.offeredAt);
    if (!entry.lastEnded || time(ended) > time(entry.lastEnded)) entry.lastEnded = ended;
    map.set(ended.invoiceId, entry);
  }
  return map;
}

/**
 * A bag the till may offer to its own driver (plan D-12): a DELIVERY bill that
 * no platform brought, still OPEN or CLOSED-unpaid. The same own-rider test as
 * `isOwnRiderBag`, without its CLOSED-only clause — a bag is usually offered
 * while it is still cooking.
 */
export function isDispatchableBill(
  bill: Pick<OffPremiseGridEntry, "channel" | "aggregatorId" | "status">,
): boolean {
  return (
    bill.channel === "DELIVERY" &&
    bill.aggregatorId === null &&
    // SETTLED (2026-10-27): a bag paid before it left can still be with a
    // driver; the driver's settle panel reaches it (the floor lists none).
    (bill.status === "OPEN" || bill.status === "CLOSED" || bill.status === "SETTLED")
  );
}

/** What the tile's chip says. */
export type DispatchChip =
  | { kind: "offered"; driverName: string; secondsLeft: number }
  | { kind: "accepted"; driverName: string }
  | { kind: "pickedUp"; driverName: string }
  | { kind: "arrived"; driverName: string }
  | { kind: "delivered"; driverName: string }
  | { kind: "failed"; driverName: string }
  | { kind: "problem"; driverName: string }
  | { kind: "notAnswered"; driverName: string }
  | { kind: "declined"; driverName: string }
  | { kind: "returned"; driverName: string }
  | { kind: "deliveryCancelled"; driverName: string };

/**
 * Milliseconds to ADD to this device's clock to read the server's: the board's
 * `serverTime` against the moment the answer arrived. A till whose clock runs
 * two minutes fast must not show an offer as already expired.
 */
export function serverSkewMs(serverTime: string, receivedAt: number): number {
  const server = Date.parse(serverTime);
  return Number.isFinite(server) ? server - receivedAt : 0;
}

/** Whole seconds an offer has left on the server's clock, never below zero. */
export function offerSecondsLeft(expiresAt: string, now: number, skewMs: number): number {
  const left = Date.parse(expiresAt) - (now + skewMs);
  return Math.max(0, Math.ceil(left / 1000));
}

/** "1:58" — minutes and two-digit seconds. */
export function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
}

/** The chip for one bill, or null when the board has nothing to say about it. */
export function dispatchChip(
  entry: BillDispatch | undefined,
  now: number,
  skewMs: number,
): DispatchChip | null {
  if (!entry) return null;
  const current = entry.current;
  if (current) {
    const driverName = current.driverName;
    if (current.problemOpen) return { kind: "problem", driverName };
    switch (current.status) {
      case "OFFERED": {
        const secondsLeft = offerSecondsLeft(current.expiresAt, now, skewMs);
        // Past the deadline the server will read it as DECLINED/EXPIRED on the
        // next read (lazy expiry, D-11); say so now rather than show 0:00.
        return secondsLeft > 0
          ? { kind: "offered", driverName, secondsLeft }
          : { kind: "notAnswered", driverName };
      }
      case "ACCEPTED":
        return { kind: "accepted", driverName };
      case "PICKED_UP":
        return { kind: "pickedUp", driverName };
      case "ARRIVED":
        return { kind: "arrived", driverName };
      case "DELIVERED":
        return { kind: "delivered", driverName };
      case "FAILED":
        return { kind: "failed", driverName };
      default:
        return null;
    }
  }
  // After pickup it came back / was called off: say so before any older offer.
  const endedKind = endedDeliveryKind(entry);
  if (endedKind && entry.endedDelivery) {
    const driverName = entry.endedDelivery.driverName;
    return endedKind === "RETURNED"
      ? { kind: "returned", driverName }
      : { kind: "deliveryCancelled", driverName };
  }
  const ended = entry.lastEnded;
  if (ended && ended.status === "DECLINED") {
    return ended.endReason === "EXPIRED"
      ? { kind: "notAnswered", driverName: ended.driverName }
      : { kind: "declined", driverName: ended.driverName };
  }
  return null;
}

/** What the till may do with a bill's dispatch right now (plan D-15, D-16, T16). */
export type DispatchActions = {
  /** No live assignment: offer it to a driver. */
  assign: boolean;
  /** Offered or accepted, not yet picked up: give it to someone else. */
  reassign: boolean;
  /** Offered or accepted, not yet picked up: take the offer back. */
  withdraw: boolean;
  /** The driver reported a problem that is still open: retry / bring it back / cancel (T5). */
  resolve: boolean;
  /**
   * Past pickup (on the way, arrived, coming back): end the delivery from the
   * till — it came back, it is called off, or the customer got it (T16) —
   * for the bag whose driver can no longer move it. Also an ACCEPTED bag
   * whose bill was already handed over (2026-10-27): the driver took it and
   * never tapped «picked up»; only «the customer got it» applies to it.
   */
  end: boolean;
};

const AFTER_PICKUP = new Set(["PICKED_UP", "ARRIVED", "FAILED"]);

export function dispatchActions(
  entry: BillDispatch | undefined,
  /** The bill is CLOSED or SETTLED: an accepted bag may be ended as delivered. */
  options: { billHandedOver?: boolean } = {},
): DispatchActions {
  const current = entry?.current ?? null;
  if (!current) return { assign: true, reassign: false, withdraw: false, resolve: false, end: false };
  const beforePickup = current.status === "OFFERED" || current.status === "ACCEPTED";
  return {
    assign: false,
    reassign: beforePickup,
    withdraw: beforePickup,
    resolve: current.problemOpen,
    end: AFTER_PICKUP.has(current.status) || (current.status === "ACCEPTED" && options.billHandedOver === true),
  };
}

/**
 * The picker's order: drivers on shift first (only they can be picked, D-8),
 * the least busy first among them, then by name; off-shift drivers after,
 * by name.
 */
export function orderDriversForPicker(
  drivers: readonly DispatchBoardDriver[],
  locale: string,
): DispatchBoardDriver[] {
  const collator = new Intl.Collator(locale);
  return [...drivers].sort((a, b) => {
    if (a.onShift !== b.onShift) return a.onShift ? -1 : 1;
    if (a.onShift && a.activeCount !== b.activeCount) return a.activeCount - b.activeCount;
    return collator.compare(a.name, b.name) || a.sellerId.localeCompare(b.sellerId);
  });
}

/** The next data-driven re-read of the board: when, and for which offers. */
export type OfferDeadline = {
  /** Milliseconds from `now`, never below zero. */
  waitMs: number;
  /** The offers whose deadline this re-read is for (all due at that moment). */
  assignmentIds: string[];
};

/**
 * When the board must be read again with nobody touching anything: the moment
 * the earliest open offer runs out (+1 s, so the server's lazy sweep has the
 * deadline behind it). Null when no offer is waiting.
 *
 * ONCE PER OFFER (G13 M1): an offer already re-read for is skipped. If the
 * server still answers it OFFERED past its deadline — the database clock a
 * little behind ours, or the sweep's `SKIP LOCKED` passing over a row an accept
 * is holding — the wait would otherwise be 0 on every answer and the board
 * would be read back-to-back: a hot loop, worse than the polling the app's
 * no-timer rule forbids. Such an offer then waits for the ordinary refreshes
 * (the realtime `floor` signal, focus, the next action).
 */
export function nextOfferDeadline(
  board: DispatchBoardResponse | undefined,
  now: number,
  skewMs: number,
  alreadyReread: ReadonlySet<string> = new Set(),
): OfferDeadline | null {
  if (!board) return null;
  let soonest: number | null = null;
  let ids: string[] = [];
  for (const assignment of board.assignments) {
    if (assignment.status !== "OFFERED" || alreadyReread.has(assignment.id)) continue;
    const ms = Math.max(0, Date.parse(assignment.expiresAt) - (now + skewMs) + 1000);
    if (soonest === null || ms < soonest) {
      soonest = ms;
      ids = [assignment.id];
    } else if (ms === soonest) {
      ids.push(assignment.id);
    }
  }
  return soonest === null ? null : { waitMs: soonest, assignmentIds: ids };
}

/** `nextOfferDeadline`'s wait alone (ms from `now`), or null with no offer waiting. */
export function nextOfferDeadlineMs(
  board: DispatchBoardResponse | undefined,
  now: number,
  skewMs: number,
): number | null {
  return nextOfferDeadline(board, now, skewMs)?.waitMs ?? null;
}

/**
 * Wraps a name interpolated into a sentence in FIRST STRONG ISOLATE … POP
 * DIRECTIONAL ISOLATE, so a driver named «سامي P13» or "Hassan 2" cannot swap
 * places with the countdown beside it in a right-to-left line.
 */
export const isolate = (text: string): string => `\u2068${text}\u2069`;

/** "19:42" — a time of day on this till, with Western digits in both languages. */
export function clockTime(iso: string, locale: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG-u-nu-latn" : "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(at);
}

/** The driver's problem reasons the till can name (contract #9); anything else reads "Something else". */
export type KnownProblemReason =
  | "NO_ANSWER"
  | "WRONG_ADDRESS"
  | "REFUSED"
  | "MISSING_OR_DAMAGED"
  | "OTHER";

export function problemReason(reason: string): KnownProblemReason {
  return reason === "NO_ANSWER" ||
    reason === "WRONG_ADDRESS" ||
    reason === "REFUSED" ||
    reason === "MISSING_OR_DAMAGED"
    ? reason
    : "OTHER";
}
